/**
 * User domain schemas, derived from the generated Zod. See ./README.md.
 */
import { z } from 'zod';

import { ClientsRetrieveResponse } from '~/data/generated/zod/clients/clients.zod';
import { ConsultantsRetrieveResponse } from '~/data/generated/zod/consultants/consultants.zod';
import { MeRetrieveResponse } from '~/data/generated/zod/me/me.zod';

/**
 * The full `UserSerializer` shape, as every nested user read emits it. `me`
 * declares the three `canAccess*` flags on top and they are optional, so this
 * base parses both.
 *
 * `email` is loosened from the generated `.email()` format check to a plain
 * string: `User.email` is `EmailField(blank=True)` with no `null=True`, so a
 * user with no email set serializes `email: ''`, which the strict format
 * check would otherwise throw on.
 *
 * `isDemo` is optional here so fixtures that predate it need not set it; a
 * missing value means a real account.
 */
export const api_userSchema = MeRetrieveResponse.extend({
  email: z.string().optional(),
  isDemo: z.boolean().optional(),
});

/**
 * The app's role. `'none'` has no server counterpart: it is synthesized in
 * `routes/_app.tsx` (`me.role ?? 'none'`) for a user who is in no group.
 */
const userRoleSchema = z.enum(['client', 'service_provider', 'consultant', 'none']);
export type UserRole = z.infer<typeof userRoleSchema>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- value exists only to derive the type below via z.infer
const userSchema = api_userSchema.omit({ role: true }).extend({ role: userRoleSchema });
export type User = z.infer<typeof userSchema>;

/**
 * The restricted `UserDisplaySerializer` shape (id/username/full_name/email/
 * phone_number) that the consultant list, and the invoice's consultant and
 * uploader, are read through. It has no standalone generated export — every
 * endpoint inlines it — so it is derived from one of them.
 *
 * `email` is loosened from the generated `.email()` format check to a plain
 * string: `User.email` is `EmailField(blank=True)` with no `null=True`, so a
 * user with no email set serializes `email: ''`, which the strict format
 * check would otherwise throw on.
 */
export const userDisplaySchema = ConsultantsRetrieveResponse.shape.consultants.element.extend({
  email: z.string().optional(),
});
export type UserDisplay = z.infer<typeof userDisplaySchema>;

/**
 * The group the signed-in user belongs to. `/clients/{id}` and
 * `/consultants/{id}` return different shapes (the consultant group adds
 * `consultants`), and which one is fetched depends on which group the user has.
 */
export type API_UserGroup =
  | z.infer<typeof ClientsRetrieveResponse>
  | z.infer<typeof ConsultantsRetrieveResponse>;
export type API_UserGroupDetailResponse = API_UserGroup;
